import { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Asterisk, Circle, Plus, Trash2 } from 'lucide-react';
import { Button } from '@tes/ui';
import { api } from '../../lib/api';
import { runSequential } from '../../lib/bulk';
import { messageOf } from '../../lib/errors';
import { useBulkSelection } from '../../lib/useBulkSelection';
import type { VariableDef } from '../../lib/types';
import { BatchNotice, BulkBar } from '../BulkActions';
import { ChoiceGroupsSection } from './ChoiceGroups';
import { DeleteVariablesDialog } from './DeleteVariablesDialog';
import { NO_VARIABLES, useVariables, variableId } from './useVariables';
import { VariableFormDialog } from './VariableFormDialog';
import { VariableTable } from './VariableTable';

/**
 * The library manager: table + add/edit/delete + bulk actions. Rendered both on
 * the Settings page and inside the editor's popup modal.
 */
export function VariableLibraryPanel() {
  const qc = useQueryClient();
  const { data, isLoading } = useVariables();
  const [editing, setEditing] = useState<VariableDef | null>(null);
  const [adding, setAdding] = useState(false);
  const [deleting, setDeleting] = useState<VariableDef[] | null>(null);

  const variables = data ?? NO_VARIABLES;
  const { sel, picked, batch, dismissBatch, finishBatch } = useBulkSelection(variables, variableId);

  const refresh = () => void qc.invalidateQueries({ queryKey: ['variables'] });

  // Items travel as a mutation variable rather than through the closure, so a
  // refetch landing mid-run can't change what the batch is working on.
  const setRequired = useMutation({
    mutationFn: ({ items, required }: { items: VariableDef[]; required: boolean }) =>
      runSequential(
        items,
        (v) => ({ id: v.id, name: v.key }),
        (v) => api.updateVariable(v.id, { required }),
      ),
    onSuccess: (results, { required }) => {
      finishBatch(required ? 'marked required' : 'marked optional')(results);
      refresh();
    },
  });

  const busy = setRequired.isPending;

  return (
    <div>
      <div className="mb-3 flex items-center justify-between gap-3">
        <p className="text-xs text-slate-400">
          Templates place variables from this library; label, required and default live here.
        </p>
        <Button size="sm" onClick={() => setAdding(true)}>
          <Plus size={14} className="mr-1" /> Add variable
        </Button>
      </div>

      {/* runSequential collects per-item failures rather than throwing, so this
          only fires on something unexpected — shown so it can't die silently. */}
      {setRequired.error && (
        <p className="mb-2 text-sm text-red-600">{messageOf(setRequired.error, 'Update failed')}</p>
      )}

      <BatchNotice batch={batch} onDismiss={dismissBatch} />

      <BulkBar
        count={sel.count}
        noun="variable"
        total={variables.length}
        onSelectAll={() => sel.setAll(true)}
        onClear={sel.clear}
      >
        <Button
          size="sm"
          variant="outline"
          disabled={busy}
          onClick={() => setRequired.mutate({ items: picked, required: true })}
        >
          <Asterisk size={14} className="mr-1" /> Required
        </Button>
        <Button
          size="sm"
          variant="outline"
          disabled={busy}
          onClick={() => setRequired.mutate({ items: picked, required: false })}
        >
          <Circle size={14} className="mr-1" /> Optional
        </Button>
        <Button size="sm" variant="danger" disabled={busy} onClick={() => setDeleting(picked)}>
          <Trash2 size={14} className="mr-1" /> Delete
        </Button>
      </BulkBar>

      {isLoading ? (
        <p className="text-sm text-slate-400">Loading…</p>
      ) : variables.length === 0 ? (
        <div className="rounded-lg border border-dashed border-slate-300 p-6 text-center text-sm text-slate-400">
          No variables yet — add your first (e.g. <code>first_name</code>).
        </div>
      ) : (
        <VariableTable
          variables={variables}
          sel={sel}
          onEdit={setEditing}
          onDelete={(v) => setDeleting([v])}
        />
      )}

      <ChoiceGroupsSection />

      {(adding || editing) && (
        <VariableFormDialog initial={editing} onClose={() => (setAdding(false), setEditing(null))} />
      )}

      {deleting && (
        <DeleteVariablesDialog
          variables={deleting}
          onClose={() => setDeleting(null)}
          onDeleted={(results) => {
            setDeleting(null);
            finishBatch('deleted')(results);
            refresh();
          }}
        />
      )}
    </div>
  );
}
